import * as React from 'react';

import { observer } from 'mobx-react';

import { Button } from '@estee/elc-buttons';
import { Body2, Heading3 } from '@estee/elc-typography';

import { CodeType } from '~constants/Constants';
import { SignInType } from '~constants/SignInTypeEnum';
import { translationKeyMap } from '~constants/TranslationKeyMap';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import { classes } from '~utils/Classes';
import { useTranslation } from '~utils/hooks/translations';
import { MessagePanel } from '~views/common/MessagePanel';
import { UserInformation } from '~views/common/UserInformation';

import * as QRCodeImage from 'qrcode.react';
import { Barcode } from './Barcode';
import { ConnectWithSocialProvider } from './ConnectWithSocialProvider';
import { QRCodeCreateOnlineAccForm } from './QRCodeCreateOnlineAccForm';

const classNames = classes('iam-thank-you');

const qrcodeClassNames = classes('iam-qrcode');
const barcodeClassName = classes('iam-barcode');
const redirectButtonWrapperClassName = classes('iam-redirect-button-wrapper');
const redirectButtonClassName = classes('iam-redirect-button');

type BarcodeProps = {
    codeValue: string;
    codeType: string;
    barCodeWidth: number;
};

type RedirectButtonSectionProps = {
    label: string;
    onClicked(): void;
};

const BarcodeView = observer(({ codeValue, codeType, barCodeWidth }: BarcodeProps) => (
    <div className={barcodeClassName}>
        <Barcode
            data={codeValue}
            options={{
                format: codeType,
                width: barCodeWidth,
                height: codeValue.length > 8 ? 200 : 100,
                fontSize: 14
            }}
        />
    </div>
));

const RedirectButtonSection = observer(({ label, onClicked }: RedirectButtonSectionProps) => (
    <div className={redirectButtonWrapperClassName}>
        <Button onClick={onClicked} className={redirectButtonClassName}>
            {label}
        </Button>
    </div>
));

export const QRCode = observer(() => {
    const qrCodeViewController = diContainer.get(serviceNames.qrCodeViewController);

    const { qrcodeTitle, qrcodeText, connectWithLine, inviteYouToJoinLine } = useTranslation();

    const {
        scannableCode: { codeValue, codeType, showCode, barCodeWidth },
        frontierErrorMessage,
        posIKalarCallbackUrl,
        userName,
        userMobile,
        userEmail,
        isMobileOnlyRegistration,
        createOnlineAccount,
        redirectButtonLabel,
        socialRedirectUrl,
        showLoginForm
    } = qrCodeViewController.data;

    // showLoginForm is a mount point config for elc-iam view only, and will be undefined for iam-social-profile
    const showCreateOnlineAccountForm =
        (!!showLoginForm || !!createOnlineAccount) && !isMobileOnlyRegistration;

    const redirectButtonClicked = () => {
        window.location.href = socialRedirectUrl;
    };

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className={classNames}>
            {frontierErrorMessage && <MessagePanel messages={[frontierErrorMessage]} />}
            {showCode && (
                <>
                    <Heading3>{qrcodeTitle}</Heading3>
                    <Body2 dangerouslySetInnerHTML={{ __html: qrcodeText }} />

                    {codeType === CodeType.QR_CODE ? (
                        <div className={qrcodeClassNames}>
                            <QRCodeImage value={codeValue} size={250} />
                        </div>
                    ) : (
                        <BarcodeView {...{ codeType, codeValue, barCodeWidth }} />
                    )}
                </>
            )}
            {posIKalarCallbackUrl && (
                <ConnectWithSocialProvider
                    provider={SignInType.LINE}
                    callbackUrl={posIKalarCallbackUrl}
                    title={connectWithLine}
                    content={inviteYouToJoinLine}
                    buttonLabelKey={translationKeyMap.continueWithLine}
                />
            )}
            <UserInformation
                showSignUpTitle={showCreateOnlineAccountForm}
                userEmail={userEmail}
                userMobile={userMobile}
                userName={userName}
            />
            {showCreateOnlineAccountForm && <QRCodeCreateOnlineAccForm />}
            {redirectButtonLabel && socialRedirectUrl && (
                <RedirectButtonSection
                    label={redirectButtonLabel}
                    onClicked={redirectButtonClicked}
                />
            )}
        </div>
    );
});
